import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "./AuthContext.jsx";
import { derivePermissions } from "../constants/permissions.js";
import { apiClient } from "../utils/apiClient.js";
import { safeStorage } from "../utils/safeStorage.js";

export const BrandContext = createContext(null);

// 공개 정적 roster는 로컬 개발에서 명시적으로 켠 경우에만 사용한다.
// 인증 API의 HTTP 오류는 이 플래그와 무관하게 항상 fail-closed다.
const PUBLIC_BRAND_FALLBACK_ENABLED =
  import.meta.env.DEV &&
  import.meta.env.VITE_ALLOW_PUBLIC_BRAND_FALLBACK === "1";

function validateRoster(payload) {
  const all = payload?.brands;
  const slugs = new Set();
  if (!Array.isArray(all) || all.some(brand => {
    if (!brand || typeof brand !== "object" || typeof brand.slug !== "string" || !brand.slug.trim() ||
        typeof brand.name !== "string" || !brand.name.trim() || slugs.has(brand.slug) ||
        (brand.allowed_users != null && (!Array.isArray(brand.allowed_users) || brand.allowed_users.some(email => typeof email !== "string")))) return true;
    slugs.add(brand.slug);
    return false;
  })) throw Object.assign(new Error("BRAND_CONFIG_INVALID"), { status: 503 });
  return all;
}

export function BrandProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [allBrands, setAllBrands] = useState([]);
  const [brands, setBrands] = useState([]);
  const [currentBrand, setCurrentBrand] = useState(null);
  const [rosterVersion, setRosterVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rosterError, setRosterError] = useState(null);
  const rosterRequestRef = useRef({ generation: 0, controller: null });
  // 18-11: 마크업 글로벌 토글 (사이드바 + ClientApp 안 활용)
  // 18-23: URL ?markup=on/off 안 동기화 — 새로고침 안 잔존 + 공유 링크 흐름 정합
  const [markupEnabled, setMarkupEnabledRaw] = useState(() => {
    if (typeof window === "undefined") return false;
    const v = new URLSearchParams(window.location.search).get("markup");
    return v === "on" || v === "1";
  });
  const setMarkupEnabled = useCallback((val) => {
    setMarkupEnabledRaw(val);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("markup", val ? "on" : "off");
    window.history.replaceState({}, "", url);
  }, []);
  // 18-13 commit 4: GA4 지표 매핑 변경 시점에 useSheets 재 fetch 트리거 (App.jsx hooks 무증가 — Context 안 단일 위치)
  const [mappingVersion, setMappingVersion] = useState(0);

  const filterBrands = useCallback(
    (all) => {
      if (!user?.email) return [];
      // S2 Task 2-A: 단일 권한 레이어(perms.isAdmin — 내부적으로 ADMIN_EMAILS isAdmin 재사용)
      if (derivePermissions({ email: user.email }).isAdmin) return all;
      return all.filter(
        (b) => b.allowed_users && b.allowed_users.includes(user.email),
      );
    },
    [user?.email],
  );

  const fetchBrands = useCallback(async () => {
    rosterRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const generation = ++rosterRequestRef.current.generation;
    rosterRequestRef.current.controller = controller;
    const isCurrent = () => generation === rosterRequestRef.current.generation;
    let timedOut = false;
    let timer;
    const deadline = new Promise((_, reject) => {
      timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
        reject(new Error("BRAND_ROSTER_TIMEOUT"));
      }, 15000);
    });
    setLoading(true);
    const applyRoster = (all, nextRosterVersion) => {
      const accessible = filterBrands(all);
      const savedSlug = safeStorage.getItem("mediaplex_selected_brand");
      const saved = accessible.find(brand => brand.slug === savedSlug);
      const selected = saved || accessible[0] || null;
      setAllBrands(all);
      setBrands(accessible);
      setCurrentBrand(selected);
      setRosterVersion(nextRosterVersion);
      setRosterError(null);
      if (selected) safeStorage.setItem("mediaplex_selected_brand", selected.slug);
      else if (savedSlug) safeStorage.removeItem("mediaplex_selected_brand");
    };
    const failRoster = (error) => {
      if (!isCurrent()) return;
      setAllBrands([]);
      setBrands([]);
      setCurrentBrand(null);
      setRosterVersion(null);
      setRosterError({ kind: timedOut ? "timeout" : [401, 403].includes(error?.status) ? "access" : "unavailable" });
    };
    try {
      // The deadline also bounds an API client waiting for session refresh.
      const data = await Promise.race([(async () => {
        const resp = await apiClient("/api/brands-proxy", { signal: controller.signal });
        if (!resp.ok) throw Object.assign(new Error(`HTTP ${resp.status}`), { status: resp.status });
        return resp.json();
      })(), deadline]);
      if (!isCurrent()) return;
      const all = validateRoster(data);
      const version = data.version === undefined ? 0
        : Number.isSafeInteger(data.version) && data.version >= 0 ? data.version : null;
      if (version === null) throw Object.assign(new Error("BRAND_CONFIG_REVISION_INVALID"), { status: 503 });
      applyRoster(all, version);
    } catch (error) {
      if (!isCurrent()) return;
      // HTTP errors and production failures never downgrade to a public roster.
      if (Number.isInteger(error?.status) || !PUBLIC_BRAND_FALLBACK_ENABLED || controller.signal.aborted) {
        failRoster(error);
      } else {
        try {
          const payload = await Promise.race([(async () => {
            const response = await fetch("/config/brands.json", { signal: controller.signal });
            if (!response.ok) throw new Error("BRAND_FALLBACK_UNAVAILABLE");
            return response.json();
          })(), deadline]);
          if (isCurrent()) applyRoster(validateRoster(payload), null);
        } catch (fallbackError) { failRoster(fallbackError); }
      }
    } finally {
      clearTimeout(timer);
      if (isCurrent()) {
        rosterRequestRef.current.controller = null;
        setLoading(false);
      }
    }
  }, [filterBrands]);

  const { loading: _authLoading } = useAuth();
  useEffect(() => {
    if (_authLoading) return; // 인증 로딩 중에는 brands 판단 보류
    if (isAuthenticated && user?.email) fetchBrands();
    else setLoading(false);
    return () => {
      rosterRequestRef.current.generation += 1;
      rosterRequestRef.current.controller?.abort();
    };
  }, [_authLoading, isAuthenticated, user?.email, fetchBrands]);

  const selectBrand = useCallback(
    (slug) => {
      const found = brands.find((b) => b.slug === slug);
      if (found) {
        setCurrentBrand(found);
        safeStorage.setItem("mediaplex_selected_brand", slug);
      }
    },
    [brands],
  );

  const refreshBrands = useCallback(async () => {
    setLoading(true);
    await fetchBrands();
  }, [fetchBrands]);

  return (
    <BrandContext.Provider
      value={{
        brands,
        currentBrand,
        rosterVersion,
        rosterError,
        loading,
        selectBrand,
        refreshBrands,
        // 18-11: 마크업 토글 + setter (App.jsx 사이드바 + ClientApp 활용)
        markupEnabled,
        setMarkupEnabled,
        // 18-13 commit 4: GA4 매핑 변경 시점에 useSheets 재 fetch 트리거
        mappingVersion,
        setMappingVersion,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error("useBrand must be used within BrandProvider");
  return ctx;
}
